import { useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { PageBody } from "../../components/PageBody";
import { PageTitle } from "../../components/Headers";
import { Loading } from "../../components/Loading";
import { getOwnedGames, getSteamRefreshStatus } from "../../api/steam";

/**
 * Calculate achievement statistics from an array of games
 * @param {Array} games - Array of game objects with achievement data
 * @returns {Object} Achievement statistics
 */
function calculateAchievementStats(games) {
    // Filter games with achievements
    const gamesWithAchievements = games.filter(
        game => game.has_achievements === true && game.n_achievements > 0
    );

    const totalAchieved = gamesWithAchievements.reduce(
        (sum, game) => sum + (game.n_achievements_earned || 0),
        0
    );

    const totalAvailable = gamesWithAchievements.reduce(
        (sum, game) => sum + (game.n_achievements || 0),
        0
    );

    const achievedAvailablePercentage = totalAvailable > 0
        ? ((totalAchieved / totalAvailable) * 100).toFixed(1)
        : 0;

    // Average completion rate: only include games with at least one achievement earned
    const gamesWithAchievementsEarned = gamesWithAchievements.filter(
        game => game.n_achievements_earned > 0
    );

    const averageCompletionPercentage = gamesWithAchievementsEarned.length > 0
        ? (gamesWithAchievementsEarned.reduce(
            (sum, game) => sum + (typeof game.achievement_perc === 'number' ? game.achievement_perc : 0),
            0
        ) / gamesWithAchievementsEarned.length).toFixed(1)
        : 0;

    // Perfect games: games with 100% achievement completion
    // Use >= 99.99 to handle floating point precision issues
    const gamesWith100Percent = gamesWithAchievements.filter(
        game => typeof game.achievement_perc === 'number' && game.achievement_perc >= 99.99
    ).length;

    return {
        totalAchieved,
        totalAvailable,
        achievedAvailablePercentage,
        averageCompletionPercentage,
        gamesWith100Percent,
        gamesWithAchievementsEarned: gamesWithAchievementsEarned.length,
        gamesWithAchievements: gamesWithAchievements.length
    };
}

/**
 * Calculate playtime statistics from an array of games
 * @param {Array} games - Array of game objects with playtime data
 * @returns {Object} Playtime statistics
 */
function calculatePlaytimeStats(games) {
    const gamesPlayed = games.filter(game => game.playtime_forever > 0).length;
    const totalPlaytime = games.reduce((sum, game) => sum + (game.playtime_forever || 0), 0);
    const averagePlaytime = gamesPlayed > 0
        ? Math.floor(totalPlaytime / gamesPlayed / 60 * 10) / 10 // Convert to hours with one decimal
        : 0;

    // Games played in the last 2 weeks (14 days)
    const twoWeeksAgo = Math.floor(Date.now() / 1000) - (14 * 24 * 60 * 60);
    const gamesPlayedLast2Weeks = games.filter(game =>
        game.rtime_last_played && game.rtime_last_played >= twoWeeksAgo
    ).length;

    return {
        gamesPlayed,
        totalPlaytime,
        averagePlaytime,
        gamesPlayedLast2Weeks
    };
}

/**
 * Calculate basic game statistics from an array of games
 * @param {Array} games - Array of game objects
 * @returns {Object} Basic game statistics
 */
function calculateGameStats(games) {
    const gameCount = games.length;
    const gamesPlayed = games.filter(game => game.playtime_forever > 0).length;
    const percentageGamesPlayed = gameCount > 0
        ? ((gamesPlayed / gameCount) * 100).toFixed(1)
        : 0;

    return {
        gameCount,
        gamesPlayed,
        percentageGamesPlayed
    };
}

/**
 * Free Game Icon Component
 * Green F/money icon indicating a free-to-play game
 */
function FreeGameIcon() {
    return (
        <div className="group relative inline-flex items-center">
            <svg
                className="h-4 w-4 text-green-600 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                Free-to-play game
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );
}

/**
 * Profile Limited Icon Component
 * Red profile icon indicating a profile-limited game
 */
function ProfileLimitedIcon() {
    return (
        <div className="group relative inline-flex items-center">
            <svg
                className="h-4 w-4 text-red-600 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
            </svg>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                Profile-limited game
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
        </div>
    );
}

export function SteamGames() {
    // State for search
    const [searchQuery, setSearchQuery] = useState("");
    
    // State for filtering (table display)
    const [showFreeGamesOnly, setShowFreeGamesOnly] = useState(false);
    const [showProfileLimitedOnly, setShowProfileLimitedOnly] = useState(false);
    const [showHasAchievementsOnly, setShowHasAchievementsOnly] = useState(false);
    const [showNoAchievementsOnly, setShowNoAchievementsOnly] = useState(false);
    
    // State for statistics inclusions (default: false = exclude both)
    const [includeFreeGamesInStats, setIncludeFreeGamesInStats] = useState(false);
    const [includeProfileLimitedInStats, setIncludeProfileLimitedInStats] = useState(false);
    
    // State for sorting
    const [sortColumn, setSortColumn] = useState('lastPlayed'); // 'playtime', 'lastPlayed', 'achievementPercent'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
    
    // State for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50); // Default to desktop
    
    // Detect screen size and set items per page
    useEffect(() => {
        const updateItemsPerPage = () => {
            setItemsPerPage(window.innerWidth < 768 ? 25 : 50);
        };
        
        updateItemsPerPage();
        window.addEventListener('resize', updateItemsPerPage);
        
        return () => window.removeEventListener('resize', updateItemsPerPage);
    }, []);
    
    // Reset to page 1 when search query, filters, or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, showFreeGamesOnly, showProfileLimitedOnly, showHasAchievementsOnly, showNoAchievementsOnly, sortColumn, sortDirection]);

    // Fetch owned games using React Query
    const { isLoading, isError, data, error } = useQuery(
        "steamOwnedGames",
        () => getOwnedGames()
    );

    // Last successful library refresh (non-blocking - page renders without it)
    const { data: refreshStatus } = useQuery(
        "steamRefreshStatus",
        () => getSteamRefreshStatus().then((response) => response.data),
        { staleTime: 5 * 60 * 1000 }
    );
    const lastRefreshedAt = refreshStatus?.last_refreshed_at;

    // Extract data (safe to access even if loading/error)
    // Wrap in useMemo to prevent useEffect dependency from changing on every render
    const rawGames = useMemo(() => data?.data?.games || [], [data?.data?.games]);

    // Filter games based on search query and filter toggles
    // Must be called before any early returns (React Hooks rule)
    const filteredGames = useMemo(() => {
        let filtered = rawGames;
        
        // Apply search filter first
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(game => 
                game.name?.toLowerCase().includes(query)
            );
        }
        
        // Apply filter toggles
        // If multiple filters are active, show games that match any condition (OR logic)
        const activeFilters = [];
        if (showFreeGamesOnly) activeFilters.push('free');
        if (showProfileLimitedOnly) activeFilters.push('profileLimited');
        if (showHasAchievementsOnly) activeFilters.push('hasAchievements');
        if (showNoAchievementsOnly) activeFilters.push('noAchievements');
        
        if (activeFilters.length > 0) {
            filtered = filtered.filter(game => {
                if (activeFilters.includes('free') && game.is_free_game === true) return true;
                if (activeFilters.includes('profileLimited') && game.is_profile_limited === true) return true;
                if (activeFilters.includes('hasAchievements') && game.has_achievements === true) return true;
                if (activeFilters.includes('noAchievements') && (game.has_achievements === false || game.has_achievements === null)) return true;
                return false;
            });
        }
        // If no filters are active, show all games (already filtered by search if applicable)
        
        return filtered;
    }, [rawGames, searchQuery, showFreeGamesOnly, showProfileLimitedOnly, showHasAchievementsOnly, showNoAchievementsOnly]);

    // Sort games based on selected column and direction
    // Must be called before any early returns (React Hooks rule)
    const games = useMemo(() => {
        const sorted = [...filteredGames].sort((a, b) => {
            let comparison = 0;
            
            if (sortColumn === 'playtime') {
                comparison = (b.playtime_forever || 0) - (a.playtime_forever || 0);
            } else if (sortColumn === 'lastPlayed') {
                comparison = (b.rtime_last_played || 0) - (a.rtime_last_played || 0);
            } else if (sortColumn === 'achievementPercent') {
                const aPercent = (a.has_achievements && typeof a.achievement_perc === 'number')
                    ? a.achievement_perc
                    : -1; // Games without achievements go to bottom
                const bPercent = (b.has_achievements && typeof b.achievement_perc === 'number')
                    ? b.achievement_perc
                    : -1;
                comparison = bPercent - aPercent;
            }
            
            // Apply sort direction
            return sortDirection === 'desc' ? comparison : -comparison;
        });
        
        return sorted;
    }, [filteredGames, sortColumn, sortDirection]);
    
    // Paginate games
    const paginatedGames = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return games.slice(startIndex, endIndex);
    }, [games, currentPage, itemsPerPage]);
    
    // Calculate pagination info
    const totalPages = Math.ceil(games.length / itemsPerPage);
    
    // Ensure current page is valid after filtering/sorting
    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);
    
    const startIndex = games.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endIndex = Math.min(currentPage * itemsPerPage, games.length);

    // Filter games for statistics calculations based on inclusion toggles
    // Must be called before any early returns (React Hooks rule)
    const gamesForStats = useMemo(() => {
        let filtered = rawGames;
        
        // Exclude free games if include toggle is false
        if (!includeFreeGamesInStats) {
            filtered = filtered.filter(game => game.is_free_game !== true);
        }
        
        // Exclude profile-limited games if include toggle is false
        if (!includeProfileLimitedInStats) {
            filtered = filtered.filter(game => game.is_profile_limited !== true);
        }
        
        return filtered;
    }, [rawGames, includeFreeGamesInStats, includeProfileLimitedInStats]);

    // Calculate statistics using helper functions with filtered games
    // Must be called before any early returns (React Hooks rule)
    const achievementStats = useMemo(() => calculateAchievementStats(gamesForStats), [gamesForStats]);
    const playtimeStats = useMemo(() => calculatePlaytimeStats(gamesForStats), [gamesForStats]);
    const gameStats = useMemo(() => calculateGameStats(gamesForStats), [gamesForStats]);

    // Loading state
    if (isLoading) {
        return <Loading loadingText="Loading Steam library..." />;
    }

    // Error state
    if (isError) {
        return (
            <PageBody>
                <PageTitle title="Steam Library" backLink="/" />
                <div className="bg-red-50 border border-red-300 rounded-lg p-6 text-center">
                    <h3 className="text-red-800 font-semibold mb-2 text-xl">
                        Unable to Load Steam Library
                    </h3>
                    <p className="text-red-600">
                        {error?.response?.data?.message || error?.message || 'An unexpected error occurred'}
                    </p>
                </div>
            </PageBody>
        );
    }

    // Helper function to format playtime
    const formatPlaytime = (minutes) => {
        if (!minutes || minutes === 0) return '-';

        // For games with less than 2 hours, show in minutes
        if (minutes < 120) {
            return `${minutes} min${minutes !== 1 ? 's' : ''}`;
        }

        // Convert to hours
        const hours = minutes / 60;
        
        // For games with less than 10 hours, show one decimal place (floored down)
        if (hours < 10) {
            const flooredHours = Math.floor(hours * 10) / 10;
            return `${flooredHours.toFixed(1)} hrs`;
        }

        // For 10+ hours, show whole hours
        const wholeHours = Math.floor(hours);
        return `${wholeHours.toLocaleString()} hrs`;
    };

    // Helper function to format last played date
    const formatLastPlayed = (rtimeLastPlayed) => {
        if (!rtimeLastPlayed) return 'Never';

        const playedDate = new Date(rtimeLastPlayed * 1000); // Convert Unix timestamp to milliseconds
        const now = new Date();

        // Reset both dates to midnight to compare calendar days, not 24-hour periods
        const playedMidnight = new Date(playedDate.getFullYear(), playedDate.getMonth(), playedDate.getDate());
        const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Calculate difference in days by comparing midnight dates
        const diffMs = todayMidnight - playedMidnight;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        // Show relative time for recent plays
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

        // For older dates, show the actual date
        return playedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Helper function to highlight matching text in game names
    const highlightMatch = (text, query) => {
        if (!query.trim()) return text;
        
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => {
            // Check if this part matches the query (case-insensitive)
            const matches = part.toLowerCase() === query.toLowerCase();
            return matches ? (
                <span 
                    key={index} 
                    className="text-gray-900"
                    style={{ backgroundColor: 'rgba(162, 110, 204, 0.25)' }}
                >
                    {part}
                </span>
            ) : (
                part
            );
        });
    };
    
    // Handler for column header clicks
    const handleSort = (column) => {
        if (sortColumn === column) {
            // Toggle direction if clicking the same column
            setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
        } else {
            // Set new column and default to descending
            setSortColumn(column);
            setSortDirection('desc');
        }
    };
    
    // Helper function to generate page numbers for pagination
    const getPageNumbers = (currentPage, totalPages) => {
        const pages = [];
        const maxVisible = 5; // Maximum number of page buttons to show (excluding first/last)
        
        if (totalPages <= maxVisible + 2) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);
            
            if (currentPage <= 3) {
                // Near the beginning: 1 2 3 4 ... max
                for (let i = 2; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('ellipsis');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // Near the end: 1 ... (max-3) (max-2) (max-1) max
                pages.push('ellipsis');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // In the middle: 1 ... (current-1) current (current+1) ... max
                pages.push('ellipsis');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('ellipsis');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };
    
    // Helper to render sort indicator
    const renderSortIndicator = (column) => {
        if (sortColumn !== column) {
            return (
                <svg className="inline-block ml-1 h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortDirection === 'desc' ? (
            <svg className="inline-block ml-1 h-3 w-3 text-badger-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        ) : (
            <svg className="inline-block ml-1 h-3 w-3 text-badger-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
        );
    };

    return (
        <PageBody>
            <Helmet>
                <title>Steam Library | VizBadger</title>
                <meta
                    name="description"
                    content="Browse games in my Steam library with playtime statistics from the Steam Web API."
                />
            </Helmet>

            <PageTitle
                title="Steam Library"
                backLink="/"
                subtitle={lastRefreshedAt
                    ? `Library data refreshed ${new Date(lastRefreshedAt).toLocaleString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    })}`
                    : false}
            />

            <div className="mb-4 text-center sm:text-left">
                <Link
                    to="/steam/insights"
                    className="inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-gray-700 underline decoration-gray-300 underline-offset-4 hover:text-gray-900"
                >
                    Completion over time, what to play next and community stats
                    <ArrowRightIcon className="h-4 w-4" />
                </Link>
            </div>

            {/* Summary Card */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg shadow-md p-6 mb-6">
                {/* Main Headline Metrics - Rule of Three */}
                {achievementStats.gamesWithAchievements > 0 && (
                    <div className="mb-6">
                        {/* Mobile: Avg Game Completion at top, then 2-column grid below */}
                        {/* Desktop: 3-column grid */}
                        <div className="block md:hidden space-y-6">
                            {/* Avg Game Completion - Top on mobile */}
                            <div className="text-center">
                                <div className="text-sm text-gray-600 mb-1">Avg Game Completion</div>
                                <div className="text-4xl font-bold text-gray-900">
                                    {achievementStats.averageCompletionPercentage}%
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Average across {achievementStats.gamesWithAchievementsEarned} games
                                </div>
                            </div>
                            {/* Other two metrics side by side */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Total Achievement %</div>
                                    <div className="text-3xl font-bold text-gray-900">
                                        {achievementStats.achievedAvailablePercentage}%
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {achievementStats.totalAchieved.toLocaleString()} / {achievementStats.totalAvailable.toLocaleString()} unlocked
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-sm text-gray-600 mb-1">Perfect Games</div>
                                    <div className="text-3xl font-bold text-gray-900">
                                        {achievementStats.gamesWith100Percent.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        100% achievement completion
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Desktop: 3-column grid */}
                        <div className="hidden md:grid md:grid-cols-3 gap-6">
                            <div className="text-left">
                                <div className="text-sm text-gray-600 mb-1">Avg Game Completion</div>
                                <div className="text-3xl font-bold text-gray-900">
                                    {achievementStats.averageCompletionPercentage}%
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Average across {achievementStats.gamesWithAchievementsEarned} games
                                </div>
                            </div>
                            <div className="text-left">
                                <div className="text-sm text-gray-600 mb-1">Total Achievement %</div>
                                <div className="text-3xl font-bold text-gray-900">
                                    {achievementStats.achievedAvailablePercentage}%
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {achievementStats.totalAchieved.toLocaleString()} / {achievementStats.totalAvailable.toLocaleString()} unlocked
                                </div>
                            </div>
                            <div className="text-left">
                                <div className="text-sm text-gray-600 mb-1">Perfect Games</div>
                                <div className="text-3xl font-bold text-gray-900">
                                    {achievementStats.gamesWith100Percent.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    100% achievement completion
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Secondary Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-4 pb-4">
                    <div className="text-center md:text-left">
                        <div className="text-xs text-gray-600 mb-1">Games Played</div>
                        <div className="text-lg font-semibold text-gray-900">
                            {gameStats.percentageGamesPlayed}%
                        </div>
                        <div className="text-xs text-gray-500">
                            {playtimeStats.gamesPlayed.toLocaleString()} of {gameStats.gameCount.toLocaleString()} games
                        </div>
                    </div>
                    <div className="text-center md:text-left">
                        <div className="text-xs text-gray-600 mb-1">Avg Playtime</div>
                        <div className="text-lg font-semibold text-gray-900">
                            {playtimeStats.averagePlaytime > 0 ? `${playtimeStats.averagePlaytime.toFixed(1)} hrs` : '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                            Per game played
                        </div>
                    </div>
                    <div className="hidden md:block text-left">
                        <div className="text-xs text-gray-600 mb-1">Recent Activity</div>
                        <div className="text-lg font-semibold text-gray-900">
                            {playtimeStats.gamesPlayedLast2Weeks.toLocaleString()} {playtimeStats.gamesPlayedLast2Weeks === 1 ? 'game' : 'games'}
                        </div>
                        <div className="text-xs text-gray-500">
                            Played in last 2 weeks
                        </div>
                    </div>
                </div>

                {/* Footer with game count and inclusion toggles */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-center pt-4 border-t border-blue-200 gap-3">
                    <div className="text-sm font-semibold text-gray-900">
                        {gameStats.gameCount.toLocaleString()} Games Owned
                        {(!includeFreeGamesInStats || !includeProfileLimitedInStats) && (
                            <span className="text-xs text-gray-500 font-normal ml-2">
                                (excluding {!includeFreeGamesInStats && !includeProfileLimitedInStats ? 'free & profile-limited' : !includeFreeGamesInStats ? 'free' : 'profile-limited'})
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-2">
                        <span className="text-xs text-gray-600">Include:</span>
                        <div className="flex items-center gap-3 md:gap-1.5">
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setIncludeFreeGamesInStats(!includeFreeGamesInStats)}
                                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1 ${
                                        includeFreeGamesInStats ? 'bg-green-500 opacity-70' : 'bg-gray-300 opacity-60'
                                    }`}
                                    aria-label="Include free games in statistics"
                                >
                                    <span
                                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                            includeFreeGamesInStats ? 'translate-x-4' : 'translate-x-0.5'
                                        }`}
                                    />
                                </button>
                                <span className="text-xs text-gray-700 flex items-center gap-1">
                                    <span>Free</span>
                                    <svg className="h-3 w-3 text-green-600 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setIncludeProfileLimitedInStats(!includeProfileLimitedInStats)}
                                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1 ${
                                        includeProfileLimitedInStats ? 'bg-red-500 opacity-70' : 'bg-gray-300 opacity-60'
                                    }`}
                                    aria-label="Include profile-limited games in statistics"
                                >
                                    <span
                                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                            includeProfileLimitedInStats ? 'translate-x-4' : 'translate-x-0.5'
                                        }`}
                                    />
                                </button>
                                <span className="text-xs text-gray-700 flex items-center gap-1">
                                    <span>Profile-limited</span>
                                    <svg className="h-3 w-3 text-red-600 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative mb-4">
                    <input
                        type="text"
                        placeholder="Search games by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-1.5 pl-9 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-badger-purple focus:border-transparent text-gray-900 placeholder-gray-500 text-sm"
                    />
                    <svg
                        className="absolute left-2.5 top-1.5 h-4 w-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>
                
                {/* Filter Toggles */}
                <div className="flex flex-wrap gap-3 items-center justify-center md:justify-start">
                    {/* Desktop: Checkboxes */}
                    <div className="hidden md:flex flex-wrap gap-4 items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showFreeGamesOnly}
                                onChange={(e) => setShowFreeGamesOnly(e.target.checked)}
                                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <span>Free games</span>
                                <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showProfileLimitedOnly}
                                onChange={(e) => setShowProfileLimitedOnly(e.target.checked)}
                                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <span>Profile-limited games</span>
                                <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showHasAchievementsOnly}
                                onChange={(e) => setShowHasAchievementsOnly(e.target.checked)}
                                className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <span>Has achievements</span>
                                <svg className="h-4 w-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                            </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showNoAchievementsOnly}
                                onChange={(e) => setShowNoAchievementsOnly(e.target.checked)}
                                className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 focus:ring-2"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <span>No achievements</span>
                                <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </span>
                        </label>
                        {(showFreeGamesOnly || showProfileLimitedOnly || showHasAchievementsOnly || showNoAchievementsOnly) && (
                            <button
                                onClick={() => {
                                    setShowFreeGamesOnly(false);
                                    setShowProfileLimitedOnly(false);
                                    setShowHasAchievementsOnly(false);
                                    setShowNoAchievementsOnly(false);
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700 underline"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                    
                    {/* Mobile: Button chips */}
                    <div className="md:hidden flex flex-col items-center gap-2">
                        <div className="flex flex-wrap gap-2 justify-center">
                            <button
                                onClick={() => setShowFreeGamesOnly(!showFreeGamesOnly)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                                    showFreeGamesOnly
                                        ? 'bg-green-100 text-green-800 border-2 border-green-600'
                                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
                                }`}
                            >
                                <svg className={`h-4 w-4 text-green-600 ${showFreeGamesOnly ? '' : 'opacity-50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>Free games</span>
                            </button>
                            <button
                                onClick={() => setShowProfileLimitedOnly(!showProfileLimitedOnly)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                                    showProfileLimitedOnly
                                        ? 'bg-red-100 text-red-800 border-2 border-red-600'
                                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
                                }`}
                            >
                                <svg className={`h-4 w-4 text-red-600 ${showProfileLimitedOnly ? '' : 'opacity-50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span>Profile-limited</span>
                            </button>
                            <button
                                onClick={() => setShowHasAchievementsOnly(!showHasAchievementsOnly)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                                    showHasAchievementsOnly
                                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-600'
                                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
                                }`}
                            >
                                <svg className={`h-4 w-4 text-yellow-600 ${showHasAchievementsOnly ? '' : 'opacity-50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                                <span>Has achievements</span>
                            </button>
                            <button
                                onClick={() => setShowNoAchievementsOnly(!showNoAchievementsOnly)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                                    showNoAchievementsOnly
                                        ? 'bg-gray-200 text-gray-800 border-2 border-gray-600'
                                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
                                }`}
                            >
                                <svg className={`h-4 w-4 text-gray-600 ${showNoAchievementsOnly ? '' : 'opacity-50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>No achievements</span>
                            </button>
                        </div>
                        {(showFreeGamesOnly || showProfileLimitedOnly || showHasAchievementsOnly || showNoAchievementsOnly) && (
                            <button
                                onClick={() => {
                                    setShowFreeGamesOnly(false);
                                    setShowProfileLimitedOnly(false);
                                    setShowHasAchievementsOnly(false);
                                    setShowNoAchievementsOnly(false);
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700 italic underline"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Games Table - Desktop */}
            <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase" rowSpan={2}>
                                    Game
                                </th>
                                <th 
                                    className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" 
                                    rowSpan={2}
                                    onClick={() => handleSort('playtime')}
                                >
                                    Total Playtime
                                    {renderSortIndicator('playtime')}
                                </th>
                                <th 
                                    className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase italic whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" 
                                    rowSpan={2}
                                    onClick={() => handleSort('lastPlayed')}
                                >
                                    Last Played
                                    {renderSortIndicator('lastPlayed')}
                                </th>
                                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 uppercase" colSpan={3}>
                                    Achievements
                                </th>
                                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 uppercase" rowSpan={2}>
                                    App ID
                                </th>
                            </tr>
                            <tr>
                                <th className="px-4 py-1 text-center text-xs font-normal text-gray-600">
                                    Has
                                </th>
                                <th className="px-4 py-1 text-center text-xs font-normal text-gray-600">
                                    Achieved
                                </th>
                                <th 
                                    className="px-4 py-1 text-center text-xs font-normal text-gray-600 cursor-pointer hover:bg-gray-200 select-none" 
                                    onClick={() => handleSort('achievementPercent')}
                                >
                                    %
                                    {renderSortIndicator('achievementPercent')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginatedGames.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                        No games found
                                    </td>
                                </tr>
                            ) : (
                                paginatedGames.map((game) => {
                                    const hasPlaytime = game.playtime_forever > 0;
                                    const hasAchievements = game.has_achievements === true;

                                    return (
                                        <tr key={game.appid} className="hover:bg-gray-50">
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-medium text-gray-900">
                                                        {game.name ? highlightMatch(game.name, searchQuery) : `App ${game.appid}`}
                                                    </span>
                                                    {game.is_free_game && <FreeGameIcon />}
                                                    {game.is_profile_limited && <ProfileLimitedIcon />}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-900">
                                                {formatPlaytime(game.playtime_forever)}
                                            </td>
                                            <td className="px-4 py-2 text-right text-xs text-gray-600 italic whitespace-nowrap">
                                                {formatLastPlayed(game.rtime_last_played)}
                                            </td>
                                            <td className="px-4 py-2 text-center text-sm">
                                                {!hasPlaytime ? (
                                                    <span className="text-gray-400">-</span>
                                                ) : hasAchievements ? (
                                                    <span className="text-green-600 font-semibold">✓</span>
                                                ) : game.n_achievements === 0 ? (
                                                    <span className="text-gray-400">✗</span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-700">
                                                {!hasPlaytime ? (
                                                    <span className="text-gray-400">-</span>
                                                ) : hasAchievements ? (
                                                    <span>{game.n_achievements_earned}/{game.n_achievements}</span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-700">
                                                {!hasPlaytime ? (
                                                    <span className="text-gray-400">-</span>
                                                ) : hasAchievements && typeof game.achievement_perc === 'number' ? (
                                                    <span>{game.achievement_perc.toFixed(1)}%</span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-center text-sm text-gray-500 font-mono">
                                                {game.appid}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Sort Bar - Mobile Only */}
            <div className="md:hidden mb-4">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-700">Sort by:</span>
                            <select
                                value={sortColumn}
                                onChange={(e) => {
                                    setSortColumn(e.target.value);
                                    setSortDirection('desc');
                                }}
                                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-badger-purple focus:border-transparent"
                            >
                                <option value="playtime">Playtime</option>
                                <option value="lastPlayed">Last Played</option>
                                <option value="achievementPercent">Achievement %</option>
                            </select>
                        </div>
                        <button
                            onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
                            className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-badger-purple focus:border-transparent"
                            aria-label={`Sort ${sortDirection === 'desc' ? 'ascending' : 'descending'}`}
                        >
                            {sortDirection === 'desc' ? (
                                <>
                                    <span className="text-gray-700">Desc</span>
                                    <svg className="h-3 w-3 text-badger-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </>
                            ) : (
                                <>
                                    <span className="text-gray-700">Asc</span>
                                    <svg className="h-3 w-3 text-badger-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Games Cards - Mobile */}
            <div className="md:hidden space-y-4">
                {paginatedGames.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
                        No games found
                    </div>
                ) : (
                    paginatedGames.map((game) => {
                        const hasPlaytime = game.playtime_forever > 0;
                        const hasAchievements = game.has_achievements === true;

                        return (
                            <div
                                key={game.appid}
                                className="bg-white border border-gray-200 rounded-lg shadow p-4"
                            >
                                <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200">
                                    <div className="flex items-center gap-1.5 pr-2 flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-900">
                                            {game.name ? highlightMatch(game.name, searchQuery) : `App ${game.appid}`}
                                        </h3>
                                        {game.is_free_game && <FreeGameIcon />}
                                        {game.is_profile_limited && <ProfileLimitedIcon />}
                                    </div>
                                    <span className="text-gray-500 font-mono text-xs whitespace-nowrap">
                                        {game.appid}
                                    </span>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Total Playtime:</span>
                                        <span className="font-medium text-gray-900">
                                            {formatPlaytime(game.playtime_forever)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Last Played:</span>
                                        <span className="text-gray-700">
                                            {formatLastPlayed(game.rtime_last_played)}
                                        </span>
                                    </div>
                                    {hasPlaytime && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Achievements:</span>
                                            <span className="text-gray-700">
                                                {hasAchievements ? (
                                                    <span>
                                                        {game.n_achievements_earned}/{game.n_achievements} ({typeof game.achievement_perc === 'number' 
                                                            ? game.achievement_perc.toFixed(1) 
                                                            : '-'}%)
                                                    </span>
                                                ) : game.n_achievements === 0 ? (
                                                    <span className="text-gray-400">✗ No</span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pagination Controls */}
            {games.length > 0 && totalPages > 1 && (
                <div className="mt-6 flex flex-col items-center gap-4">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-badger-purple"
                            aria-label="Previous page"
                        >
                            &lt;
                        </button>
                        {getPageNumbers(currentPage, totalPages).map((page, index) => {
                            if (page === 'ellipsis') {
                                return (
                                    <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-gray-500">
                                        ...
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-badger-purple ${
                                        currentPage === page
                                            ? 'bg-badger-purple text-white border-badger-purple hover:bg-purple-600'
                                            : 'bg-white text-gray-700'
                                    }`}
                                >
                                    {page}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-badger-purple"
                            aria-label="Next page"
                        >
                            &gt;
                        </button>
                    </div>
                </div>
            )}

            {/* Footer Note */}
            {games.length > 0 && (
                <div className="mt-4 text-sm text-gray-500 text-center">
                    {searchQuery.trim() ? (
                        <>Showing {startIndex}-{endIndex} of {games.length} games (filtered from {gameStats.gameCount} total)</>
                    ) : (
                        <>Showing {startIndex}-{endIndex} of {games.length} games</>
                    )}
                </div>
            )}
            {games.length === 0 && searchQuery.trim() && (
                <div className="mt-6 text-sm text-gray-500 text-center">
                    No games found matching "{searchQuery}"
                </div>
            )}
        </PageBody>
    );
}
